import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Send, Hash, Shield, MessageCircle, ArrowLeft, Users, Brain, Smile, ChevronDown,
} from 'lucide-react';
import { api, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import { Avatar, Empty, SectionHead, SkeletonList } from '../components/ui';
import TriviaChatCard from '../components/TriviaChatCard';

const QUICK = ['🔥', '😂', '💪', '🥳', '👏', '🤯', '❤️', '⚔️'];

export default function Chat() {
  const { roomType, roomId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [typing, setTyping] = useState([]);
  const [online, setOnline] = useState({ count: 0, users: [] });
  const [loading, setLoading] = useState(true);
  const [atBottom, setAtBottom] = useState(true);
  const [unseen, setUnseen] = useState(0);
  const [showEmoji, setShowEmoji] = useState(false);

  const boxRef = useRef();
  const typingTimers = useRef({});
  const typingSent = useRef(0);

  const { data: rooms } = useQuery({
    queryKey: ['chatRooms'],
    queryFn: () => api.get('/chat/rooms/mine').then(r => r.data),
  });

  const { data: groupInfo } = useQuery({
    queryKey: ['chatGroup', roomId],
    queryFn: () => api.get(`/groups/${roomId}`).then(r => r.data),
    enabled: roomType === 'group' && !!roomId,
  });

  const { data: guildInfo } = useQuery({
    queryKey: ['chatGuild', roomId],
    queryFn: () => api.get(`/guilds/${roomId}`).then(r => r.data),
    enabled: roomType === 'guild' && !!roomId,
  });

  const scrollToBottom = useCallback((smooth = true) => {
    requestAnimationFrame(() => {
      boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    });
    setUnseen(0);
  }, []);

  /* ---------------- LOAD + SOCKET ---------------- */
  useEffect(() => {
    if (!roomType || !roomId) return;
    setMsgs([]); setLoading(true); setUnseen(0);

    api.get(`/chat/${roomType}/${roomId}`)
      .then(r => { setMsgs(r.data); setTimeout(() => scrollToBottom(false), 60); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));

    const s = getSocket(); if (!s) return;
    const room = `${roomType}:${roomId}`;
    s.emit('join', room);
    s.emit('presence:who', { roomType, roomId }, (p) => p && setOnline(p));

    const presenceInt = setInterval(() => {
      s.emit('presence:who', { roomType, roomId }, (p) => p && setOnline(p));
    }, 20000);

    const onMsg = (m) => {
      if (m.room_type !== roomType || String(m.room_id) !== String(roomId)) return;
      setMsgs(v => (v.some(x => x.id === m.id) ? v : [...v, m]));
      if (boxRef.current) {
        const near = boxRef.current.scrollHeight - boxRef.current.scrollTop - boxRef.current.clientHeight < 140;
        if (near || m.user_id === user.id) scrollToBottom();
        else setUnseen(u => u + 1);
      }
    };

    const onTyping = ({ userId, username, display_name }) => {
      if (userId === user.id) return;
      const name = display_name || username;
      setTyping(t => (t.includes(name) ? t : [...t, name]));
      clearTimeout(typingTimers.current[name]);
      typingTimers.current[name] = setTimeout(
        () => setTyping(t => t.filter(x => x !== name)), 2600);
    };

    s.on('chat:message', onMsg);
    s.on('chat:typing', onTyping);

    return () => {
      s.off('chat:message', onMsg);
      s.off('chat:typing', onTyping);
      s.emit('leave', room);
      clearInterval(presenceInt);
      Object.values(typingTimers.current).forEach(clearTimeout);
      typingTimers.current = {};
    };
  }, [roomType, roomId, user.id, scrollToBottom]);

  function handleScroll() {
    const b = boxRef.current; if (!b) return;
    const near = b.scrollHeight - b.scrollTop - b.clientHeight < 120;
    setAtBottom(near);
    if (near) setUnseen(0);
  }

  function send() {
    const s = getSocket();
    const body = text.trim();
    if (!s || !body) return;
    s.emit('chat:send', { roomType, roomId, body }, (res) => {
      if (res?.error) toast.error(res.error);
    });
    setText(''); setShowEmoji(false);
    scrollToBottom();
  }

  function onType(v) {
    setText(v);
    const now = Date.now();
    if (now - typingSent.current > 1600) {
      typingSent.current = now;
      getSocket()?.emit('chat:typing', { roomType, roomId });
    }
  }

  /* ================= ROOM LIST ================= */
  if (!roomType) {
    return (
      <div className="space-y-4 animate-fade-up">
        <div className="card p-5 bg-gradient-to-br from-brand-500 to-grape-600 text-white">
          <MessageCircle size={24} className="mb-2" />
          <h1 className="font-display text-2xl font-extrabold">Live chats</h1>
          <p className="text-white/85 text-sm mt-1">
            City rooms like the old IRC days. Type <b>!trivia</b> to launch a live quiz for everyone. 🧠
          </p>
        </div>

        {rooms?.guild && (
          <>
            <SectionHead title="Guild" />
            <Link to={`/chat/guild/${rooms.guild.id}`} className="card-h p-3 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-grape-500 to-indigo-700 grid place-items-center text-white">
                <Shield size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{rooms.guild.name}</p>
                <p className="text-[11px] text-ink-400 font-semibold">Private guild chat</p>
              </div>
              <ChevronDown size={16} className="-rotate-90 text-ink-300" />
            </Link>
          </>
        )}

        <SectionHead title="City rooms" sub={`${rooms?.groups?.length || 0} joined`} />
        {!rooms ? <SkeletonList n={4} h="h-16" />
          : !rooms.groups?.length
            ? <Empty icon={MessageCircle} title="Join a city group"
                sub="City groups have live chat rooms and live trivia."
                action={<Link to="/groups" className="btn-primary btn-sm">Browse cities</Link>} />
            : <div className="space-y-2">
              {rooms.groups.map(g => (
                <Link key={g.id} to={`/chat/group/${g.id}`} className="card-h p-3 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-brand-100 text-brand-600 grid place-items-center">
                    <Hash size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{g.handle}</p>
                    <p className="text-[11px] text-ink-400 font-semibold">{g.name} · live chat + trivia</p>
                  </div>
                  <span className="chip-grape"><Brain size={9} /> trivia</span>
                </Link>))}
            </div>}
      </div>
    );
  }

  /* ================= ROOM ================= */
  const title = roomType === 'guild'
    ? (guildInfo?.name || 'Guild chat')
    : (groupInfo?.handle || 'City chat');
  const subtitle = roomType === 'guild'
    ? `[${guildInfo?.tag || '...'}] · ${online.count} online`
    : `${groupInfo?.name || ''} · ${online.count} online`;

  return (
    <div className="card overflow-hidden flex flex-col h-[calc(100dvh-8.5rem)] lg:h-[calc(100vh-7rem)] animate-fade-up relative">
      {/* HEADER */}
      <div className="px-3 py-2.5 border-b border-ink-100 flex items-center gap-2 bg-white/95 backdrop-blur z-20">
        <button onClick={() => nav('/chat')} className="p-1.5 rounded-lg hover:bg-ink-100 lg:hidden">
          <ArrowLeft size={17} />
        </button>
        <div className={`w-9 h-9 rounded-xl grid place-items-center text-white shrink-0
          ${roomType === 'guild' ? 'bg-gradient-to-br from-grape-500 to-indigo-700' : 'bg-gradient-to-br from-brand-400 to-brand-600'}`}>
          {roomType === 'guild' ? <Shield size={16} /> : <Hash size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate">{title}</p>
          <p className="text-[10px] text-ink-400 font-semibold truncate">
            {typing.length
              ? `${typing.slice(0, 2).join(', ')}${typing.length > 2 ? ` +${typing.length - 2}` : ''} typing…`
              : subtitle}
          </p>
        </div>
        <div className="flex -space-x-2">
          {online.users.slice(0, 4).map(u => (
            <img key={u.id}
              src={u.avatar_url || `https://api.dicebear.com/9.x/adventurer/svg?seed=${u.username}`}
              title={`@${u.username}`} alt=""
              className="w-7 h-7 rounded-full ring-2 ring-white object-cover bg-ink-100" />))}
          {online.count > 4 && (
            <div className="w-7 h-7 rounded-full ring-2 ring-white bg-ink-200 grid place-items-center text-[9px] font-black text-ink-600">
              +{online.count - 4}
            </div>)}
        </div>
      </div>

      {/* MESSAGES */}
      <div ref={boxRef} onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-3 space-y-2 bg-gradient-to-b from-ink-50 to-white">

        {/* LIVE TRIVIA CARD — sticky above the messages */}
        {roomType === 'group' && (
          <div className="sticky top-0 z-10 -mx-1 px-1">
            <TriviaChatCard groupId={Number(roomId)} />
          </div>
        )}

        {loading ? <SkeletonList n={5} h="h-12" />
          : !msgs.length ? (
            <div className="text-center py-10">
              <MessageCircle size={30} className="mx-auto text-ink-200 mb-2" />
              <p className="text-sm font-bold text-ink-400">No messages yet</p>
              <p className="text-xs text-ink-400 mt-1">
                Say hi 👋 or type <b className="text-grape-600">!trivia</b> to start a live quiz.
              </p>
            </div>
          ) : msgs.map((m, i) => {
            const mine = m.user_id === user.id;
            const bot = m.is_bot || m.user_id === 0 || m.username === 'TriviaBot';
            const prev = msgs[i - 1];
            const grouped = prev && prev.user_id === m.user_id && !bot && !prev.is_bot
              && (new Date(m.created_at) - new Date(prev.created_at)) < 120000;

            /* ---- BOT MESSAGE ---- */
            if (bot) {
              return (
                <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center my-2">
                  <div className="max-w-[92%] bg-gradient-to-r from-indigo-500 to-grape-600 text-white
                    rounded-2xl px-3.5 py-2.5 shadow-lg">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Brain size={11} />
                      <span className="text-[10px] font-black opacity-90 uppercase tracking-wide">
                        {m.display_name || 'Trivia Bot'}
                      </span>
                    </div>
                    <p className="text-[13px] font-semibold whitespace-pre-wrap leading-snug">
                      {renderBold(m.body)}
                    </p>
                  </div>
                </motion.div>
              );
            }

            /* ---- USER MESSAGE ---- */
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex gap-2 ${mine ? 'flex-row-reverse' : ''} ${grouped ? '-mt-1' : ''}`}>
                <div className="w-[30px] shrink-0">
                  {!grouped && (
                    <Link to={`/u/${m.username}`}>
                      <Avatar src={m.avatar_url} name={m.username} size={30} />
                    </Link>)}
                </div>
                <div className={`max-w-[75%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                  {!grouped && (
                    <div className={`flex items-center gap-1.5 px-1 ${mine ? 'flex-row-reverse' : ''}`}>
                      <Link to={`/u/${m.username}`} className="text-[10px] font-black text-ink-500 hover:underline">
                        {m.display_name || m.username}
                      </Link>
                      {m.guild_tag && <span className="text-[9px] font-black text-grape-500">[{m.guild_tag}]</span>}
                      {m.level && <span className="text-[9px] font-bold text-ink-400">L{m.level}</span>}
                      {m.role === 'admin' && <span className="text-[8px] font-black bg-ink-900 text-white px-1 rounded">ADMIN</span>}
                    </div>)}
                  <div className={`px-3 py-2 text-sm font-medium break-words
                    ${mine
                      ? 'bg-gradient-to-br from-brand-500 to-brand-600 text-white rounded-2xl rounded-br-md'
                      : 'bg-white text-ink-800 rounded-2xl rounded-bl-md shadow-soft border border-ink-100'}`}>
                    {m.body}
                  </div>
                  {!grouped && (
                    <span className="text-[9px] text-ink-300 px-1 mt-0.5 font-semibold">
                      {timeAgo(m.created_at)}
                    </span>)}
                </div>
              </motion.div>
            );
          })}
      </div>

      {/* JUMP TO BOTTOM */}
      <AnimatePresence>
        {!atBottom && (
          <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            onClick={() => scrollToBottom()}
            className="absolute bottom-20 right-4 z-20 flex items-center gap-1.5 bg-ink-900 text-white
              px-3 py-2 rounded-full shadow-xl text-xs font-black">
            <ChevronDown size={14} />
            {unseen > 0 ? `${unseen} new` : 'Latest'}
          </motion.button>)}
      </AnimatePresence>

      {/* EMOJI BAR */}
      <AnimatePresence>
        {showEmoji && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-ink-100 bg-white px-2 py-2 flex gap-1.5 overflow-x-auto no-scrollbar">
            {QUICK.map(e => (
              <button key={e} onClick={() => { setText(t => t + e); }}
                className="w-9 h-9 rounded-lg bg-ink-100 text-lg active:scale-90 transition shrink-0">{e}</button>))}
          </motion.div>)}
      </AnimatePresence>

      {/* COMPOSER */}
      <div className="p-2 border-t border-ink-100 flex gap-1.5 bg-white safe-bottom">
        <button onClick={() => setShowEmoji(v => !v)}
          className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 transition
            ${showEmoji ? 'bg-brand-100 text-brand-600' : 'bg-ink-100 text-ink-500'}`}>
          <Smile size={17} />
        </button>
        <input className="input !py-2.5 flex-1"
          placeholder={roomType === 'group' ? 'Message… (try !trivia)' : 'Message…'}
          value={text} maxLength={1000}
          onChange={e => onType(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <button onClick={send} disabled={!text.trim()}
          className="btn-primary !px-4 shrink-0"><Send size={16} /></button>
      </div>
    </div>
  );
}

/* renders **bold** segments coming from the trivia bot */
function renderBold(text = '') {
  return String(text).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <b key={i} className="font-black">{part.slice(2, -2)}</b>
      : <span key={i}>{part}</span>);
}